import React, { useState, useEffect } from "react";
import { useListGovernances } from "./api";
import DataTable from "@components/DataTable";
import {
  useV2DeleteProjectGovernanceMutation,
  useV2ListServiceCategorysForAccountQuery,
  useV2UpdateProjectGovernanceMutation,
} from "@generated";
import { withErrorReason, withFailureReasons } from "@components/CatalogIndex";
import {
  deleteGovernances,
  saveGovernancePositions,
} from "./api/governanceWrites";
import { ColumnDef } from "@tanstack/react-table";
import SmallSpinner from "@components/SmallSpinner/SmallSpinner";
import "./styles/style.css";
import { useLocation, useNavigate } from "react-router";
import { handleAlert } from "@utils/helperFunctions";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { Governance } from "../types/Governance";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faTrashCan } from "@fortawesome/pro-solid-svg-icons";
import ScopeStackModal from "@components/ScopeStackModal/ScopeStackModal";
import { AdminListCard } from "@components/CatalogIndex/AdminListCard";

const Show = ({
  project_id,
  account,
  permission,
  project,
  projectPhases,
}): JSX.Element => {
  const [selectedGovernanceIds, setSelectedGovernanceIds] = useState<string[]>(
    []
  );
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const navigate = useNavigate();
  const location = useLocation();
  const [errorMessages, setErrorMessages] = useState<string | string[]>(
    "Something went wrong! Your changes could not be saved at this time."
  );
  const [showFailAlert, setShowFailAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState(
    "Project Governance saved successfully!"
  );
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [reloading, setReloading] = useState(false);

  // Bulk Delete States
  const [deleteGovernance] = useV2DeleteProjectGovernanceMutation();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [deleteQueue, setDeleteQueue] = useState<string[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [updateProjectGovernance] = useV2UpdateProjectGovernanceMutation();

  const [governances, setGovernances] = useState<Governance[]>([]);

  const {
    data: lobs,
    isLoading: lobsLoading,
    isError: lobsErr,
  } = useV2ListServiceCategorysForAccountQuery({});

  const {
    data: governanceData,
    governancesError,
    governancesLoading,
    governancesFetching,
    pageMeta,
    refetch,
  } = useListGovernances({
    page: {
      number: pageNumber,
      size: pageSize,
    },
    include: [
      "resource",
      "project-phase",
      "project-resource",
      "project",
      "service-category",
    ],
    lobs: lobs && lobs.data && !lobsLoading ? lobs.data : [],
    filter: { active: true, project: String(project_id) },
    projectPhases: projectPhases || [],
  });

  useEffect(() => {
    window.history.replaceState({}, "");
    refetch();
    if (location?.state?.edit) {
      setSuccessMessage("Project Governance updated successfully!");
      setShowSuccessAlert(true);
    }
    if (location?.state?.new) {
      setSuccessMessage("Project Governance created successfully!");
      setShowSuccessAlert(true);
    }
  }, [pageNumber]);

  const onUpdateGovernances = (governances) => {
    setGovernances(governances);
  };

  useEffect(() => {
    if (!governancesLoading && !governancesFetching) {
      setGovernances(governanceData);
    }
  }, [governancesLoading, governancesFetching]);

  useEffect(() => {
    const updateGovernances = async () => {
      if (!governances.some((g) => g?.isChanged)) return;
      const { error } = await saveGovernancePositions(
        governances,
        (id, position) =>
          updateProjectGovernance({
            id,
            body: {
              data: {
                id,
                type: "project-governances",
                attributes: { position },
              },
            },
          }).unwrap()
      );
      if (error) {
        setErrorMessages(
          withErrorReason("Could not save the governance order.", error)
        );
        setShowFailAlert(true);
      }
      // Either way, show the order the server actually has.
      refetch();
    };

    updateGovernances();
  }, [governances]);

  const handleDelete = () => {
    setDeleteQueue([...selectedGovernanceIds]);
    setShowConfirmationModal(true);
  };

  const confirmDelete = () => {
    setIsDeleting(true);
    if (deleteQueue.length === 0) return;

    deleteGovernances(deleteQueue, governances, (id) =>
      deleteGovernance({ id }).unwrap()
    ).then((failures) => {
      if (failures.length) {
        setErrorMessages(
          withFailureReasons(
            `Could not delete ${failures.length} of ${deleteQueue.length} governance${
              deleteQueue.length > 1 ? "s" : ""
            }.`,
            failures
          )
        );
        setShowFailAlert(true);
      } else {
        setSuccessMessage(
          `Project Governance${
            deleteQueue.length > 1 ? "s" : ""
          } deleted successfully!`
        );
        setShowSuccessAlert(true);
      }
      setIsDeleting(false);
      setShowConfirmationModal(false);
      setSelectedGovernanceIds([]);
      setDeleteQueue([]);
      refetch();
    });
  };

  const handleConfirm = () => {
    confirmDelete();
  };

  const handleCancel = () => {
    setShowConfirmationModal(false);
    setDeleteQueue([]);
  };

  const getConfirmationMessage = () => {
    return (
      <>
        <div
          style={{
            color: "#1C2655",
            fontSize: "14px",
          }}
        >
          {isDeleting ? (
            <SmallSpinner />
          ) : (
            <span style={{ fontWeight: "700" }}>{`You are about to delete ${
              selectedGovernanceIds.length
            } governance item${
              selectedGovernanceIds.length > 1 ? "s" : ""
            }.`}</span>
          )}
          <ul style={{ marginTop: "10px" }}>
            {selectedGovernanceIds.map((govIdToDelete) => {
              let govName = governances.find(
                (gov) => gov.id == govIdToDelete
              )?.description;
              return <li>{govName}</li>;
            })}
          </ul>
          This action cannot be undone. Do you wish to proceed?
        </div>
      </>
    );
  };

  const governanceColumns: ColumnDef<Governance>[] = [
    {
      header: "Governance",
      cell(props) {
        return (
          <div
            className="editLink"
            onClick={() => {
              if (props.row.original.id !== 0) {
                navigate(
                  `/projects/${project_id}/project_governances/edit/${props.row.original.id}`,
                  {
                    state: { governance: props.row.original },
                  }
                );
              }
            }}
          >
            {props.row.original.description}{" "}
            {props.row.original.id !== 0 && <FontAwesomeIcon icon={faPencil} />}
          </div>
        );
      },
    },
    {
      header: "Phase",
      cell(props) {
        return props.row.original.phaseForDisplay || "";
      },
    },
    {
      header: "Resource",
      cell(props) {
        return props.row.original.resource?.label !== "Select Resource"
          ? props.row.original.resource?.label
          : "";
      },
    },
    {
      header: "Calculate Hrs By",
      cell(props) {
        return props.row.original.calculationTypeForDisplay;
      },
    },
    {
      header: "Hours",
      cell(props) {
        return governances.length > 0 ? props.row.original.hours : "";
      },
    },
  ];

  if (permission === "none") {
    return (
      <div className="governancePage">
        You do not have permmission to view this page. Please contact your
        administrator for assistance.
      </div>
    );
  }

  const totalServiceEffort = (
    <div className="totalEffort">
      <strong>Total Service Effort:</strong>&nbsp;
      {project?.data?.attributes?.["total-effort"]}&nbsp;hrs
    </div>
  );

  return (
    <div className="governancePage">
      {handleAlert(
        showFailAlert,
        errorMessages,
        setShowFailAlert,
        "warning",
        ToastAlert
      )}
      {handleAlert(
        showSuccessAlert,
        successMessage,
        setShowSuccessAlert,
        "success",
        ToastAlert
      )}
      <AdminListCard
        title="Governance"
        onNew={
          permission !== "view" &&
          project?.data?.attributes?.status === "building"
            ? () =>
                navigate(`/projects/${project_id}/project_governances/new`, {
                  state: {},
                })
            : undefined
        }
        newButtonLabel="Add Governance"
        batchActions={[
          {
            icon: faTrashCan,
            label: "Delete",
            onClick: handleDelete,
            variant: "danger",
          },
        ]}
        selectedCount={selectedGovernanceIds.length}
        extraInfo={totalServiceEffort}
      >
        {!governances ||
        governancesLoading ||
        !lobs ||
        lobsLoading ||
        governancesFetching ? (
          <SmallSpinner />
        ) : (
          <DataTable
            classForTable="governanceTable"
            data={
              governances && governances?.length > 0
                ? governances.sort((a, b) =>
                    a.position && b.position ? a.position - b.position : 0 - 0
                  )
                : [
                    {
                      id: 0,
                      description: "Add governances to get started",
                    },
                  ]
            }
            setData={onUpdateGovernances}
            columns={governanceColumns}
            selectable={
              governances.length > 0 &&
              permission !== "view" &&
              project?.data?.attributes?.status === "building"
            }
            striped
            hover
            totalPages={pageMeta.pageCount}
            totalRows={pageMeta.recordCount}
            currentPage={pageNumber}
            setCurrentPage={setPageNumber}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            paginationEnabled
            pageSizeEnabled
            selectedIds={selectedGovernanceIds}
            setSelectedIds={setSelectedGovernanceIds}
            dragAndDrop={governances.length > 1}
          />
        )}
        <ScopeStackModal
          modalTitle="Delete Governance"
          modalBody={getConfirmationMessage()}
          handleButton2Click={isDeleting ? () => null : handleConfirm}
          handleClose={handleCancel}
          show={showConfirmationModal}
          handleButton1Click={handleCancel}
          deleteModal={true}
          button1Text={"Cancel"}
          button2Text={"Delete"}
        />
      </AdminListCard>
    </div>
  );
};

export default Show;
